import React from 'react';
import { i18n } from 'config/locales';

import { Summary, DateSummary } from 'components/Summary';
import { SUBSTANCE_USE, SUBSTANCE_USE_ALCOHOL_NEGATIVE } from 'config/formSections/substanceUse';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import NegativeImpact from './NegativeImpact';

const sectionConfig = {
  key: SUBSTANCE_USE_ALCOHOL_NEGATIVE.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_ALCOHOL_NEGATIVE.name,
  storeKey: SUBSTANCE_USE_ALCOHOL_NEGATIVE.storeKey,
};

export class NegativeImpacts extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateHasImpacts = this.updateHasImpacts.bind(this);
    this.updateList = this.updateList.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      HasImpacts: this.props.HasImpacts,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      Impact: this.props.Impact,
      WhenAffected: this.props.WhenAffected,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateHasImpacts = (values) => {
    this.update({
      HasImpacts: values,
      List: values.value === 'Yes' ? this.props.List : [],
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
      Impact: values.value === 'Yes' ? this.props.Impact : {},
      WhenAffected: values.value === 'Yes' ? this.props.WhenAffected : {},
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  updateImpact = (values) => {
    this.update({ Impact: values });
  };

  updateWhenAffected = (values) => {
    this.update({ WhenAffected: values });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const occurred = DateSummary(o.Occurred);

    return Summary({
      type: i18n.t('substance.alcohol.negativeImpact.collection.itemType'),
      index,
      left: occurred,
      right: null,
      placeholder: i18n.t('substance.alcohol.negativeImpact.collection.summary'),
    });
  };

  render() {
    const { errors } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div
        attribute="HasImpacts"
        className="section-content negative-impacts"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_NEGATIVE.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.alcohol.negative')}</h1>
        <Branch
          {...this.props.HasImpacts}
          attribute="HasImpacts"
          name="has_impacts"
          label={i18n.t('substance.pvq.alcohol.heading.negativeImpact')}
          labelSize="h4"
          className="has-impacts"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHasImpacts} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.HasImpacts.value === 'Yes'
          && (
          <div className="negative-impact">
            <Field
              attribute="Impact"
              title={i18n.t('substance.pvq.alcohol.negativeImpact.heading.impact')}
              className="no-margin-bottom"
              adjustFor="textarea"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                {...this.props.Impact}
                attribute="Impact"
                name="Impact"
                className="impact"
                onUpdate={this.updateImpact}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>

            <Field
              attribute="WhenAffected"
              title={i18n.t('substance.pvq.alcohol.negativeImpact.heading.whenAffected')}
              help="substance.alcohol.negativeImpact.help.whenAffected"
              adjustFor="daterange"
              scrollIntoView={this.props.scrollIntoView}
            >
              <DateRange
                {...this.props.WhenAffected}
                attribute="WhenAffected"
                name="WhenAffected"
                className="WhenAffected"
                minDateEqualTo
                onUpdate={this.updateWhenAffected}
                onError={this.props.onError}
                required={this.props.required}
                hideFromDay
                hideToDay
              />
            </Field>
          </div>
          )}
      </div>
    ) : (
      <div
        attribute="HasImpacts"
        className="section-content negative-impacts"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_NEGATIVE.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.alcohol.negative')}</h1>
        <Branch
          attribute="HasImpacts"
          name="has_impacts"
          label={i18n.t('substance.alcohol.heading.negativeImpact')}
          labelSize="h4"
          className="has-impacts"
          {...this.props.HasImpacts}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateHasImpacts} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />

        <Show when={this.props.HasImpacts.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.alcohol.negativeImpact.collection.description')}
            appendTitle={i18n.t('substance.alcohol.negativeImpact.collection.appendTitle')}
            appendLabel={i18n.t('substance.alcohol.negativeImpact.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <NegativeImpact
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

NegativeImpacts.defaultProps = {
  HasImpacts: {},
  List: EmptyListInstance,
  Comments: {},
  SummaryComments: {},
  Impact: {},
  WhenAffected: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'alcohol/negative',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(NegativeImpacts, sectionConfig);
